/// Owns the Pixi.Application + pan/zoom viewport, and the layer graph
/// for a single level. Drives the GameSession via a tick loop, refreshes
/// the AircraftLayer each frame, and bridges Pixi clicks into UI state
/// via a `select(id)` callback.
///
/// World-space pixel canvas is fixed at WORLD_PX × WORLD_PX. The
/// projector maps `level.worldSizeInNM` to that width, so all NM-based
/// math (turn radius, vector lengths) lands in a stable pixel scale
/// regardless of the user's screen.

import { Application, Container } from 'pixi.js'
import { Viewport } from 'pixi-viewport'
import { CoordinateProjector } from '../geo/CoordinateProjector'
import type { LevelBundle } from '../data/loadLevel'
import { BasemapLayer } from './BasemapLayer'
import { CarrierLayer } from './CarrierLayer'
import { OverlayLayer } from './OverlayLayer'
import { RiversLayer } from './RiversLayer'
import { RunwayLayer } from './RunwayLayer'
import { ApproachCourseLayer } from './ApproachCourseLayer'
import { AircraftLayer } from './AircraftLayer'
import { TakeoffQueueLayer } from './TakeoffQueueLayer'
import { BuildingsLayer } from './BuildingsLayer'
import { ContoursLayer } from './ContoursLayer'
import { HandoffBoundaryLayer } from './HandoffBoundaryLayer'
import { DistanceRingsLayer } from './DistanceRingsLayer'
import { TerrainLayer } from './TerrainLayer'
import { WaypointsLayer } from './WaypointsLayer'
import { WeatherLayer } from './WeatherLayer'
import { radarTheme } from './theme'
import { GameSession } from '../game/GameSession'
import { startTickLoop } from '../game/tickLoop'
import {
  commandGoAround,
  evaluateApproachClearance,
  evaluateLandingClearance,
  issueLandingClearance,
  type ApproachClearanceResult,
  type ApproachEvaluation,
  type GoAroundResult,
  type LandingClearanceResult,
  type LandingEvaluation,
} from '../game/Autopilot'
import {
  commandCancelHold,
  commandClearedForApproach,
  commandDirectTo,
  commandHold,
  commandTakeoff,
} from '../game/CommandService'
import { activeRunwayForLanding } from '../game/runwaySelection'
import { isPositionInStorm } from '../game/CollisionDetector'
import type { Aircraft } from '../game/Aircraft'

const WORLD_PX = 2000

export interface RadarSceneHandlers {
  onSelect(id: string | null): void
  onTick(): void
  /// Spontaneous (non-command) ATC chatter from the sim — spawn
  /// check-ins, fix crossings, level-offs, etc. Forwarded to the
  /// readback toast stack.
  onReadback?(text: string): void
}

export interface RadarSceneOptions {
  /// Difficulty preset name (matching LevelData.difficultySettings keys).
  difficultyName?: string
  /// Direct override for rate-of-aircraft-per-hour (URL `?rate=`).
  rateOverride?: number
}

export class RadarScene {
  readonly app: Application
  readonly viewport: Viewport
  readonly world: Container
  readonly projector: CoordinateProjector
  readonly session: GameSession
  readonly aircraftLayer: AircraftLayer

  private stopTicks: () => void
  private resizeHandler: () => void

  private constructor(args: {
    app: Application
    viewport: Viewport
    world: Container
    projector: CoordinateProjector
    session: GameSession
    aircraftLayer: AircraftLayer
    stopTicks: () => void
    resizeHandler: () => void
  }) {
    this.app = args.app
    this.viewport = args.viewport
    this.world = args.world
    this.projector = args.projector
    this.session = args.session
    this.aircraftLayer = args.aircraftLayer
    this.stopTicks = args.stopTicks
    this.resizeHandler = args.resizeHandler
  }

  static async mount(
    container: HTMLElement,
    bundle: LevelBundle,
    handlers: RadarSceneHandlers,
    options: RadarSceneOptions = {},
  ): Promise<RadarScene> {
    const app = new Application()
    await app.init({
      background: radarTheme.background,
      antialias: true,
      resizeTo: container,
      resolution: window.devicePixelRatio || 1,
      autoDensity: true,
    })
    container.appendChild(app.canvas)

    // app.screen always reports CSS pixels (regardless of devicePixelRatio).
    // Using renderer.width/height instead causes a 2× hit-area mismatch
    // on Retina displays: events only fire in the top-left quartile.
    const viewport = new Viewport({
      screenWidth: app.screen.width,
      screenHeight: app.screen.height,
      worldWidth: WORLD_PX,
      worldHeight: WORLD_PX,
      events: app.renderer.events,
    })
    viewport
      .drag()
      .pinch()
      .wheel({ smooth: 5 })
      .decelerate()
      // Wide zoom band so phone players can pinch all the way in
      // (data tag legible) and zoom out to the full radar. Desktop
      // mouse-wheel users naturally stay in the middle of the range.
      .clampZoom({ minScale: 0.2, maxScale: 12 })
    app.stage.addChild(viewport)
    // Background-tap to deselect. Only fires when the tap landed on
    // the viewport itself (not on an aircraft child) — without that
    // guard, the handler swallowed the pointer-down that pixi-viewport
    // needs to start drag, breaking pan + zoom on first interaction.
    viewport.on('pointertap', (e) => {
      if (e.target === viewport) handlers.onSelect(null)
    })

    const focusAirport =
      bundle.level.airports.find((a) => a.name === bundle.level.initialFocusAirportName) ??
      bundle.level.airports[0]
    if (!focusAirport) throw new Error('Level has no airports')

    const projector = new CoordinateProjector({
      worldCenter: focusAirport.centerCoordinate,
      worldSizeInNM: bundle.level.worldSizeInNM,
      width: WORLD_PX,
      height: WORLD_PX,
      magneticVariationDegrees: focusAirport.magneticVariation,
    })

    const world = new Container()
    viewport.addChild(world)

    const pxPerNM = WORLD_PX / bundle.level.worldSizeInNM

    // Construct the session first — its effectiveWindDirection
    // (which may be a per-session random roll) drives the localizer
    // layer below.
    const session = new GameSession({
      bundle,
      projector,
      pxPerNM,
      worldPx: WORLD_PX,
      difficultyName: options.difficultyName,
      rateOverride: options.rateOverride,
    })

    world.addChild(new BasemapLayer(bundle.basemap, projector))
    let contoursLayer: ContoursLayer | null = null
    if (bundle.contours) {
      contoursLayer = new ContoursLayer(bundle.contours, projector)
      world.addChild(contoursLayer)
      // Counter-scale elevation labels so they stay readable at any
      // zoom. Initial sync against the current viewport scale, then
      // re-sync on every `zoomed` event (pinch / wheel / programmatic
      // zoom). Without this the 16pt labels render as ~5px tall at
      // zoom-out-to-fit-world on a phone — effectively invisible.
      contoursLayer.updateLabelStyles(viewport.scale.x)
      viewport.on('zoomed', () => {
        contoursLayer?.updateLabelStyles(viewport.scale.x)
      })
    }
    if (bundle.rivers) world.addChild(new RiversLayer(bundle.rivers, projector))
    // Overlay sits above water so roads bridge lakes (matches iOS
    // ordering — overlay LineStrings at zPosition -19.65 vs lakes
    // at -19.7); town polygon fills are at 6% alpha so they're
    // effectively invisible over water either way.
    if (bundle.overlay) world.addChild(new OverlayLayer(bundle.overlay, projector))
    if (bundle.buildings) world.addChild(new BuildingsLayer(bundle.buildings, projector))
    if (bundle.level.terrainObstacles && bundle.level.terrainObstacles.length > 0) {
      world.addChild(new TerrainLayer(bundle.level.terrainObstacles, projector))
    }
    world.addChild(new DistanceRingsLayer(bundle.level.airports, projector, pxPerNM))
    if (bundle.level.liveTraffic) {
      // The handoff boundary — edge of your airspace. Centered on the world,
      // a touch inside the scope edge so traffic crosses it before it's lost.
      const boundaryNM = session.liveBoundaryRadiusNM
      world.addChild(
        new HandoffBoundaryLayer(WORLD_PX / 2, WORLD_PX / 2, boundaryNM * pxPerNM),
      )
    }
    const approachCourseLayer = new ApproachCourseLayer(
      bundle.level,
      projector,
      pxPerNM,
      session.effectiveWindDirection,
    )
    world.addChild(approachCourseLayer)
    const runwayLayer = new RunwayLayer(bundle.level.airports, projector)
    world.addChild(runwayLayer)
    // Airport names keep their size on screen as the player zooms out.
    viewport.on('zoomed', () => runwayLayer.updateLabelScale(viewport.scale.x))
    // Carrier(s) — moving airports get their own layer that
    // translates per-tick. Static airports skip this entirely.
    const carrierLayers: CarrierLayer[] = []
    for (const ap of bundle.level.airports) {
      if (ap.motion) {
        const layer = new CarrierLayer(ap, projector, pxPerNM)
        world.addChild(layer)
        carrierLayers.push(layer)
      }
    }
    world.addChild(new WaypointsLayer(bundle.level.waypoints, projector))

    // Weather sits between the radar geography and the aircraft — so
    // storms wash over the basemap/rivers but never occlude airplanes.
    const weatherLayer = new WeatherLayer(pxPerNM)
    world.addChild(weatherLayer)

    const aircraftLayer = new AircraftLayer({ onSelect: (id) => handlers.onSelect(id) })
    world.addChild(aircraftLayer)
    aircraftLayer.update(session.aircrafts)

    // Pending-takeoffs layer sits between the runway and the live
    // aircraft so queue dots appear *behind* threshold markings but
    // *in front of* the basemap. Reads `session.takeoffQueueEntries()`
    // every tick — flat list, grouped+positioned internally.
    const takeoffQueueLayer = new TakeoffQueueLayer()
    world.addChild(takeoffQueueLayer)
    takeoffQueueLayer.update(session.takeoffQueueEntries())

    // Subscribe to ticks: refresh sprites + notify React for chrome update.
    const unsubscribe = session.addListener({
      onTick: (dtSec) => {
        aircraftLayer.update(session.aircrafts)
        takeoffQueueLayer.update(session.takeoffQueueEntries())
        weatherLayer.update(session.thunderstorms, dtSec)
        // Carriers — translate the layer container by the live
        // motion delta. Geometry is built at INITIAL position, so
        // a single Pixi position assignment per moving airport per
        // frame is all the work.
        for (const layer of carrierLayers) {
          const delta = session.airportTranslationPx(layer.airportName)
          if (delta) layer.setTranslation(delta)
        }
        // Live wind sim: rebuild the localizer-arrow layer when
        // the wind has crossed the runway-re-evaluation threshold
        // since the last build. Already-cleared aircraft keep
        // their cleared runway — only the visible "active runway"
        // markings shift.
        if (session.consumeWindRunwayReEvalRequest()) {
          approachCourseLayer.setWindDirection(session.effectiveWindDirection)
        }
        handlers.onTick()
      },
      onReadback: handlers.onReadback,
    })

    const stopTickLoop = startTickLoop(session)
    const stopTicks = () => {
      unsubscribe()
      stopTickLoop()
    }

    // Center on focus airport, apply level's initial zoom (default 1.8).
    const center = projector.project(focusAirport.centerCoordinate, true)
    const zoom = bundle.level.initialZoomLevel ?? 1.8
    viewport.moveCenter(center.x, center.y)
    viewport.setZoom(zoom, true)
    // setZoom emits no `zoomed`, so the names take the starting zoom here.
    runwayLayer.updateLabelScale(viewport.scale.x)

    const resizeHandler = () => {
      viewport.resize(app.screen.width, app.screen.height, WORLD_PX, WORLD_PX)
    }
    window.addEventListener('resize', resizeHandler)

    return new RadarScene({
      app,
      viewport,
      world,
      projector,
      session,
      aircraftLayer,
      stopTicks,
      resizeHandler,
    })
  }

  /// Try to clear the given aircraft to land on the level's active
  /// runway (wind-driven, falling back to the airport's first runway
  /// when no wind is set). Returns the validation result so the UI
  /// can surface failure reasons.
  clearToLand(aircraftId: string): LandingClearanceResult {
    const aircraft = this.session.aircrafts.find((a) => a.id === aircraftId)
    if (!aircraft) return { ok: false, reason: 'invalid_runway' }
    const choice = this.pickLandingRunway(aircraft)
    if (!choice) return { ok: false, reason: 'invalid_runway' }
    return issueLandingClearance({
      aircraft,
      airport: choice.airport,
      runway: choice.runway,
      approachHeadingDeg: choice.headingDeg,
      projector: this.projector,
      pxPerNM: this.session.pxPerNM,
    })
  }

  /// Pure evaluation of "would clear-to-land succeed right now?" used
  /// by the chrome to enable/disable the button + label it with the
  /// rejection reason. Delegates to the same evaluator that issue()
  /// uses against the same active-runway pick, so the two can never
  /// disagree.
  evaluateClearToLand(aircraftId: string): LandingEvaluation | null {
    const aircraft = this.session.aircrafts.find((a) => a.id === aircraftId)
    if (!aircraft) return null
    const choice = this.pickLandingRunway(aircraft)
    if (!choice) return null
    return evaluateLandingClearance({
      aircraft,
      airport: choice.airport,
      runway: choice.runway,
      approachHeadingDeg: choice.headingDeg,
      projector: this.projector,
      pxPerNM: this.session.pxPerNM,
    })
  }

  /// The runway end the cleared-to-land flow validates against — the
  /// goal airport's active runway (wind-driven) when the aircraft
  /// has a `.land` goal, else the level's focus airport, else the
  /// first airport. Critically NOT a heading-proximity pick: the
  /// aircraft has to be flying TO the active runway, not whichever
  /// runway happens to fit its current heading.
  private pickLandingRunway(aircraft: Aircraft) {
    const level = this.session.bundle.level
    return activeRunwayForLanding({
      airports: level.airports,
      goalAirportName:
        aircraft.goal.land?.airportName ?? aircraft.goal.arrival?.airportName,
      focusAirportName: level.initialFocusAirportName,
      // Always-defined wind: published value when the level has one,
      // session random roll otherwise. Same value the localizer
      // layer drew with, so the wedge the player aligns to is the
      // end the clearance gate accepts.
      fixedWindDirection: this.session.effectiveWindDirection,
    })
  }

  /// Issue a "Cleared for approach" against the active runway. Engages
  /// the localizer-intercept autopilot — heading is driven by
  /// `tickApproachIntercept` until the player issues Cleared to land
  /// (or vectors away, which cancels the intercept).
  commandClearedForApproach(aircraftId: string): ApproachClearanceResult {
    const aircraft = this.session.aircrafts.find((a) => a.id === aircraftId)
    if (!aircraft) return { ok: false, reason: 'invalid_runway' }
    const choice = this.pickLandingRunway(aircraft)
    if (!choice) return { ok: false, reason: 'invalid_runway' }
    return commandClearedForApproach({
      aircraft,
      airport: choice.airport,
      runway: choice.runway,
      approachHeadingDeg: choice.headingDeg,
      projector: this.projector,
      pxPerNM: this.session.pxPerNM,
    })
  }

  /// Pure evaluation of "would Cleared for approach succeed right
  /// now?" — drives the contextual button visibility (45° offset,
  /// 15 NM range against the active runway).
  evaluateClearedForApproach(aircraftId: string): ApproachEvaluation | null {
    const aircraft = this.session.aircrafts.find((a) => a.id === aircraftId)
    if (!aircraft) return null
    const choice = this.pickLandingRunway(aircraft)
    if (!choice) return null
    return evaluateApproachClearance({
      aircraft,
      airport: choice.airport,
      runway: choice.runway,
      approachHeadingDeg: choice.headingDeg,
      projector: this.projector,
      pxPerNM: this.session.pxPerNM,
    })
  }

  /// Issue a takeoff clearance for an aircraft sitting at the threshold.
  /// Returns `{ ok: false, reason }` if the aircraft isn't on the ground,
  /// its goal lacks a target waypoint, or a thunderstorm is parked over
  /// the runway threshold.
  commandTakeoff(aircraftId: string): { ok: boolean; reason?: string } {
    const aircraft = this.session.aircrafts.find((a) => a.id === aircraftId)
    if (!aircraft) return { ok: false, reason: 'not_found' }
    // Storm-over-runway check (matches iOS isThunderstormOverRunway).
    // The aircraft is sitting at the threshold, so its current position
    // is the threshold position. Departures wait until the cell drifts
    // off — refuse and let the player retry.
    if (
      isPositionInStorm({
        position: aircraft.position,
        storms: this.session.weather.storms,
        pxPerNM: this.session.pxPerNM,
      })
    ) {
      return { ok: false, reason: 'thunderstorm' }
    }
    return commandTakeoff(aircraft, this.session.bundle.level, this.projector)
  }

  /// Issue a direct-to to a named waypoint, or pass null to clear.
  commandDirectTo(aircraftId: string, waypointName: string | null): { ok: boolean; reason?: string } {
    const aircraft = this.session.aircrafts.find((a) => a.id === aircraftId)
    if (!aircraft) return { ok: false, reason: 'not_found' }
    return commandDirectTo(aircraft, waypointName, this.session.bundle.level, this.projector)
  }

  /// Issue "Hold at <fix>". If the aircraft is on landing approach,
  /// the hold also triggers a go-around.
  commandHold(
    aircraftId: string,
    waypointName: string,
  ): { ok: boolean; reason?: string; goAround?: boolean } {
    const aircraft = this.session.aircrafts.find((a) => a.id === aircraftId)
    if (!aircraft) return { ok: false, reason: 'not_found' }
    return commandHold({
      aircraft,
      waypointName,
      level: this.session.bundle.level,
      projector: this.projector,
      airports: this.session.bundle.level.airports,
    })
  }

  /// Cancel an active hold. ok=false if the aircraft wasn't holding.
  commandCancelHold(aircraftId: string): { ok: boolean } {
    const aircraft = this.session.aircrafts.find((a) => a.id === aircraftId)
    if (!aircraft) return { ok: false }
    return commandCancelHold(aircraft)
  }

  /// Abort an in-progress landing. Returns ok=false if the aircraft
  /// isn't currently on `landing` status. On success, retries every
  /// non-empty takeoff queue — the runway this aircraft was
  /// approaching may now be promotable for a queued departure, and
  /// without this trigger the queue would sit until the next routine
  /// event (rotation, landing, new enqueue) re-fired promotion.
  /// Mirrors iOS commit 96ccc0f's go-around path on the 2.4 branch.
  commandGoAround(aircraftId: string): GoAroundResult {
    const aircraft = this.session.aircrafts.find((a) => a.id === aircraftId)
    if (!aircraft) return { ok: false, reason: 'not_on_approach' }
    const result = commandGoAround(aircraft, this.session.bundle.level.airports)
    if (result.ok) {
      this.session.tryPromoteAllQueues()
    }
    return result
  }

  /// Scale wall-clock dt by `m` before each tick. Powers the time-
  /// compression chrome buttons (1× / 2× / 4× / 8×).
  setTimeMultiplier(m: number): void {
    this.session.setTimeMultiplier(m)
  }

  /// Pan + zoom the viewport so the named aircraft sits at the center.
  /// Used by the flight strip to jump between aircraft. No-op if the
  /// aircraft has gone off-radar.
  panToAircraft(aircraftId: string): void {
    const aircraft = this.session.aircrafts.find((a) => a.id === aircraftId)
    if (!aircraft) return
    this.viewport.animate({
      time: 250,
      position: { x: aircraft.position.x, y: aircraft.position.y },
      ease: 'easeInOutSine',
    })
  }

  /// Recenter the viewport on the level's home (focus) airport at
  /// the level's initial zoom. Pans only — gestural pan/zoom from
  /// the player isn't reset, so a 4× pinched-in player doesn't lose
  /// their zoom; only the scroll offset rewinds. (Set zoom too if
  /// you want a "fit-to-world" reset.)
  panToHome(): void {
    const level = this.session.bundle.level
    const focus =
      level.airports.find((a) => a.name === level.initialFocusAirportName) ??
      level.airports[0]
    if (!focus) return
    const center = this.projector.project(focus.centerCoordinate, true)
    this.viewport.animate({
      time: 250,
      position: { x: center.x, y: center.y },
      ease: 'easeInOutSine',
    })
  }

  destroy(): void {
    window.removeEventListener('resize', this.resizeHandler)
    this.stopTicks()
    this.session.dispose()
    this.app.destroy(true, { children: true })
  }
}

